import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ActionType, County, Plan, SearchResultPlan } from "../../@types";
import { RouteComponentProps, withRouter } from "react-router";
import URI from "urijs";
import {
  CONSTANTS,
  logError,
  parsePlanLongIdIntoUrlPartForApi,
  useTranslate,
  isPdPlanType,
  useFormatMessage,
  makePlanLongId,
} from "../../helpers";
import { getPlan } from "../../api";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppStore } from "../../app/store";
import {
  AppPage,
  ActionsBar,
  EnrollmentDownAlert,
  FocusableHeader,
  LoadingMask,
} from "../../components";

import routes from "../../app/routes";
import Overview from "./Overview";
import BenefitsCosts from "./BenefitsCosts";
import ExtraBenefits from "./ExtraBenefits";
import DrugCosts from "./DrugCosts";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";
import { PlanHeader, PlanRadioInput } from "./components";

const PlanSwitchOptions: FunctionComponent<{
  plans: Plan[];
  selectedPlan: Plan | undefined;
  setSelectedPlan: (plan: Plan) => void;
}> = ({ plans, setSelectedPlan, selectedPlan }) => {
  const t = useTranslate();

  return (
    <div className="mct-hide-print ds-u-margin-bottom--4 ds-u-margin-top--7 ds-u-lg-display--none">
      <div className="ds-u-text-align--center ds-u-border-bottom--1 ds-u-padding-bottom--2">
        <h2 className="ds-u-margin-bottom--2">
          {t("plan_compare.select_a_plan_name")}
        </h2>
        <p className="ds-text-heading--lg ds-u-margin-top--0 ds-u-font-weight--normal">
          <FormattedMessage
            id="plan_compare.you_are_comparing"
            values={{ total: plans.length }}
          />
        </p>
      </div>
      <form className="ds-u-margin-top--6">
        {plans.map(plan => (
          <PlanRadioInput
            plan={plan}
            key={plan.id}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
          />
        ))}
      </form>
    </div>
  );
};

const PlanComparePage: FunctionComponent<RouteComponentProps> = ({
  location,
  history,
}) => {
  const [plans, updatePlans] = useState<Plan[]>([]);
  const {
    state: { year, lis, futureLis, county, compareList, prescriptions },
    dispatch,
  } = useContext(AppStore.AppContext);
  const { mctCurrentPlanYear = "" } = useFlags();
  const [selectedPlan, setSelectedPlan] = useState<Plan>();
  const plansRef = useRef(plans);

  const onPlanRemove = (planToRemove: Plan): void => {
    const removePlanFilter = (plan: Plan | SearchResultPlan): boolean =>
      plan.id !== planToRemove.id;

    updatePlans(plans.filter(removePlanFilter));

    dispatch({
      type: ActionType.UPDATE_COMPARE_LIST,
      payload: compareList.filter(removePlanFilter),
    });
  };

  useEffect(() => {
    if (!compareList.length) {
      history.push(routes.searchResults);
    }
  }, [compareList]);

  const validatePlanIds = (idList: string | (string | null)[] | null): void => {
    if (!idList) {
      throw new Error("Plans list variable was not provided in query");
    }

    // the URI api returns a string value when a single "plans" variable is provided
    if (typeof idList === "string") {
      throw new Error("Only one plan ID was provided");
    }

    if (idList.length < CONSTANTS.compareFooter.minPlansToCompare) {
      throw new Error(
        `The query string to the plan compare path requires a minimum of ${CONSTANTS.compareFooter.minPlansToCompare} IDs`
      );
    }

    // ensure that each longId can be split into fours
    if (!idList.every(longId => longId?.split("-").length === 4)) {
      throw new Error(
        `One or more plan ID values could not be split into four segments`
      );
    }
  };

  const getIdsFromQuery = (): string[] => {
    const hasQueryString = location && location.search;
    const locationURI = new URI(location.search);

    // true, if the query string contains atleast one plans variable
    const hasPlansList = locationURI.hasQuery("plans");

    let planIdList = null;
    if (hasQueryString && hasPlansList) {
      planIdList = locationURI.search(true).plans;
    } else {
      throw new Error("Plans list variable was not provided in query");
    }

    validatePlanIds(planIdList);
    return planIdList as string[];
  };

  useEffect(() => {
    let planIdList: string[] = [];

    try {
      planIdList = getIdsFromQuery();
    } catch (e) {
      console.error(
        "The following exception occured while validating query parameters to the plan compare page: ",
        e.message
      );
    }

    const lisToSend =
      year !== mctCurrentPlanYear.toString() && futureLis ? futureLis : lis;

    Promise.all(
      (planIdList as string[]).map(id => {
        const apiURL = parsePlanLongIdIntoUrlPartForApi(id);
        return getPlan(apiURL, lisToSend ? { lis: lisToSend } : {});
      })
    )
      .then(plans =>
        plans.reduce((acc, plan) => {
          if (plan) acc.push(plan);
          return acc;
        }, [] as Plan[])
      )
      .then(planData => {
        updatePlans(planData);
        plansRef.current = planData;
      })
      .catch(error => {
        logError("Failed to retrieve plan (PlanComparisonPage)", error);
      });
  }, []);

  const CountyName: FunctionComponent<{
    county: County;
    className?: string;
  }> = ({ county, className }) => (
    <p className={className}>
      {county.name}, {county.state}
    </p>
  );

  const determineSelectedPlan = () => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

    if (isDesktop) {
      setSelectedPlan(undefined);
    } else {
      // Keep from resetting the plan if changing width below desktop breakpoint
      setSelectedPlan(prevPlanValue => {
        return prevPlanValue || plansRef.current[0];
      });
    }
  };

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

    if (plans.length && !isDesktop) {
      setSelectedPlan(plans[0]);
    }
  }, [plans]);

  useEffect(() => {
    window.addEventListener("resize", determineSelectedPlan);

    return (): void =>
      window.removeEventListener("resize", determineSelectedPlan);
  }, []);

  const fm = useFormatMessage();
  const heading = fm(
    { id: "plan_compare.you_are_comparing" },
    { total: plans.length }
  );

  return plans.length ? (
    <AppPage
      heading={heading}
      headingClass="ds-u-display--none"
      bannerClassName="ds-u-padding--0"
    >
      <FocusableHeader className="e2e-pc-comparing" text={heading} srOnly />

      <ActionsBar
        extraContent={
          county && (
            <CountyName
              className="PlanComparePage__countyName--tablet"
              county={county}
            />
          )
        }
        extraContentClassName="ds-u-lg-display--none ds-u-display--none ds-u-md-display--block ds-u-text-align--center"
        to={routes.searchResults}
        withPrint
        printEventAction="Find Plans - Compare Plans"
      >
        {<FormattedHTMLMessage id="plan_compare.go_back_to_plan_results" />}
      </ActionsBar>

      {county && (
        <CountyName
          className="PlanComparePage__countyName--mobileDesktop ds-u-md-display--none ds-u-lg-display--block"
          county={county}
        />
      )}

      <EnrollmentDownAlert className="ds-u-margin-bottom--2 ds-u-lg-margin-bottom--4 ds-u-margin-top--2 ds-u-lg-margin-top--0" />

      <PlanSwitchOptions
        plans={plans}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
      />
      {selectedPlan && (
        <div className="ds-u-lg-display--none mct-hide-print">
          <PlanHeader
            plan={selectedPlan}
            handleGoToPlan={() => {
              history.push(
                `${routes.planDetails.replace(
                  ":id",
                  makePlanLongId(selectedPlan)
                )}`
              );
            }}
            noLeftBorder
          />
        </div>
      )}
      <table className="PlanComparePage__table">
        <thead>
          <tr>
            <td className="PlanComparePage__floatingHeader">
              <div className="PlanComparePage__planHeader ds-u-border-left--0"></div>
            </td>
            {plans.map(plan => (
              <td className="PlanComparePage__floatingHeader" key={plan.id}>
                <PlanHeader
                  plan={plan}
                  handleGoToPlan={() => {
                    history.push(
                      `${routes.planDetails.replace(
                        ":id",
                        makePlanLongId(plan)
                      )}`
                    );
                  }}
                  onPlanRemove={onPlanRemove}
                />
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          <Overview plans={plans} selectedPlan={selectedPlan} />
          {!isPdPlanType(plans[0].plan_type) && (
            <BenefitsCosts plans={plans} selectedPlan={selectedPlan} />
          )}
          {!isPdPlanType(plans[0].plan_type) && (
            <ExtraBenefits plans={plans} selectedPlan={selectedPlan} />
          )}
          {prescriptions.length > 0 && (
            <DrugCosts plans={plans} selectedPlan={selectedPlan} />
          )}
        </tbody>
      </table>
    </AppPage>
  ) : (
    <LoadingMask loading={plans.length === 0} />
  );
};

export default withRouter(PlanComparePage);
